import { useState } from 'react';
import { Code2, Loader2, Copy, Check, TrendingUp, Activity, CrosshairIcon, BarChart3, Layers } from 'lucide-react';
import { Highlight, themes } from 'prism-react-renderer';
import { generateIndicator } from '../../services/api';
import toast from 'react-hot-toast';

export default function CodeGenerator({ onLimitReached, onGenerateSuccess }) {
  const [prompt, setPrompt] = useState('');
  const [language, setLanguage] = useState('pinescript');
  const [generatedCode, setGeneratedCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const languages = [
    { value: 'pinescript', label: 'Pine Script', ext: 'pine', disabled: false },
    { value: 'mql4', label: 'MetaTrader (MQL4)', ext: 'mq4', disabled: true, comingSoon: true },
  ];

  const promptSuggestions = [
    {
      id: 1,
      icon: TrendingUp,
      label: 'EMA Crossover',
      prompt: 'Create an EMA 21 & 55 crossover indicator with BUY/SELL signals and alerts. Show the EMAs on the chart and display arrows when they cross.',
    },
    {
      id: 2,
      icon: Activity,
      label: 'RSI Signals',
      prompt: 'Create an RSI overbought/oversold indicator with alerts. Mark overbought zone above 70 and oversold zone below 30 with background colors and alerts.',
    },
    {
      id: 3,
      icon: TrendingUp,
      label: 'Golden Cross',
      prompt: 'Create a Moving Average Golden Cross/Death Cross detector using 50 and 200 period moving averages. Show visual signals and alerts when crossovers occur.',
    },
    {
      id: 4,
      icon: Layers,
      label: 'Support & Resistance',
      prompt: 'Create a Support and Resistance level finder that automatically detects and draws key price levels based on recent highs and lows.',
    },
    {
      id: 5,
      icon: BarChart3,
      label: 'MACD Signals',
      prompt: 'Create a MACD histogram with signal line crossover alerts. Show bullish and bearish crossovers with different colors and alert notifications.',
    },
  ];

  const handleSuggestionClick = (suggestionPrompt) => {
    setPrompt(suggestionPrompt);
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      toast.error('Please enter a prompt');
      return;
    }

    setLoading(true);
    setGeneratedCode('');

    try {
      const response = await generateIndicator({ prompt, language });
      setGeneratedCode(response.data.data.code);
      toast.success('Code generated successfully!');

      if (onGenerateSuccess) {
        onGenerateSuccess();
      }
    } catch (error) {
      // Handle 403 - Daily limit reached (new daily system)
      if (error.response?.status === 403 || error.response?.data?.limitReached) {
        const message = error.response?.data?.message || 'Daily limit reached! Upgrade to VIP for 1000 monthly generations or Premium for unlimited access.';
        toast.error(message, { duration: 5000 });
        if (onLimitReached) {
          onLimitReached();
        }
      }
      // Handle 429 - Daily limit reached (legacy system)
      else if (error.response?.status === 429) {
        toast.error('Daily limit reached! Upgrade to VIP or Premium for more generations.');
        if (onLimitReached) {
          onLimitReached();
        }
      }
      else {
        toast.error(error.response?.data?.message || 'Failed to generate code');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedCode);
      setCopied(true);
      toast.success('Code copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy code');
    }
  };

  const getLanguageForHighlight = () => {
    const map = {
      pinescript: 'javascript',
      mql4: 'cpp',
    };
    return map[language] || 'javascript';
  };

  return (
    <div className="card dark:!bg-black dark:border-gray-800">
      <div className="flex items-center gap-2 mb-6">
        <Code2 size={24} className="text-primary-600" />
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Generate Trading Indicator</h2>
      </div>

      <div className="space-y-4">
        {/* Language Selector */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Programming Language
          </label>
          <div className="grid grid-cols-2 gap-2">
            {languages.map((lang) => (
              <button
                key={lang.value}
                onClick={() => !lang.disabled && setLanguage(lang.value)}
                disabled={lang.disabled}
                className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 relative ${language === lang.value
                  ? 'bg-primary-600 text-white shadow-md'
                  : lang.disabled
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-600 cursor-not-allowed opacity-60'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
              >
                <span>{lang.label}</span>
                {lang.comingSoon && (
                  <span className="ml-2 inline-block px-2 py-0.5 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">
                    Coming Soon
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Quick Prompts Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Quick Prompts
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {promptSuggestions.map((suggestion) => {
              const Icon = suggestion.icon;
              return (
                <button
                  key={suggestion.id}
                  onClick={() => handleSuggestionClick(suggestion.prompt)}
                  disabled={loading}
                  className="group relative flex items-center gap-2 px-3 py-2.5 bg-gradient-to-r from-cyan-50 to-teal-50 hover:from-cyan-100 hover:to-teal-100 border border-cyan-200 rounded-lg transition-all duration-200 hover:shadow-md hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                >
                  <div className="flex items-center justify-center w-8 h-8 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow duration-200">
                    <Icon size={18} className="text-cyan-600" />
                  </div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-700 group-hover:text-gray-900">
                    {suggestion.label}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Click any button to auto-fill the prompt below
          </p>
        </div>

        {/* Prompt Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Describe Your Indicator
          </label>
          <textarea
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="E.g., Create a moving average crossover indicator with RSI filter..."
            className="input-field min-h-32 resize-y"
            disabled={loading}
          />
          <p className="mt-2 text-sm text-gray-500">
            Be specific about what you want. Include details like indicator type, parameters, and conditions.
          </p>
        </div>

        {/* Generate Button */}
        <button
          onClick={handleGenerate}
          disabled={loading || !prompt.trim()}
          className="btn-primary w-full flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <Loader2 size={20} className="animate-spin" />
              Generating...
            </>
          ) : (
            <>
              <Code2 size={20} />
              Generate Code
            </>
          )}
        </button>

        {/* Generated Code Display */}
        {generatedCode && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Generated Code</h3>
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg transition-colors duration-200"
              >
                {copied ? (
                  <>
                    <Check size={16} className="text-green-600" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy size={16} />
                    Copy
                  </>
                )}
              </button>
            </div>

            <div className="relative">
              <Highlight
                theme={themes.vsDark}
                code={generatedCode}
                language={getLanguageForHighlight()}
              >
                {({ className, style, tokens, getLineProps, getTokenProps }) => (
                  <pre
                    className={`${className} rounded-lg p-4 overflow-x-auto text-sm`}
                    style={style}
                  >
                    {tokens.map((line, i) => (
                      <div key={i} {...getLineProps({ line })}>
                        <span className="inline-block w-8 text-gray-500 select-none">
                          {i + 1}
                        </span>
                        {line.map((token, key) => (
                          <span key={key} {...getTokenProps({ token })} />
                        ))}
                      </div>
                    ))}
                  </pre>
                )}
              </Highlight>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
